import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from 'react-router-dom';

import { API_URL } from '../../../api/api';

const AddCashLoanLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem('token');

    if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    const [form, setForm] = useState({
        branchname: '',
        groupname: '',
        membername: '',
        unit: 1,
        loanamount: '',
        opendate: '',
        guarantor: '',
        staffid: '',
        membernames: '',
        loanamountwithint: '',
        coapplicantmember: '',
        guarantors: '',
        tenure: '',
        collectiontype: 'Weekly',
        description: '',
        close_day: '',
        status: 'active',
        emi: '',
    });

    const [branches, setBranches] = useState([]);
    const [groups, setGroups] = useState([]);
    const [members, setMembers] = useState([]);
    const [products, setProducts] = useState([]);
    const [staffs, setStaffs] = useState([]);


    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (id) {
            fetchCashLoan();
        }
    }, [id]);

    useEffect(() => {
        const loanAmountWithInterest = parseFloat(form.loanamountwithint) || 0;
        const tenure = parseInt(form.tenure) || 0;

        if (loanAmountWithInterest > 0 && tenure > 0) {
            const calculatedEMI = loanAmountWithInterest / tenure;
            setForm(prev => ({ ...prev, emi: calculatedEMI.toFixed(2) }));
        } else {
            setForm(prev => ({ ...prev, emi: '' }));
        }
    }, [form.loanamountwithint, form.tenure]);


    const fetchCashLoan = async () => {
        try {
            const res = await axios.get(`${API_URL}/cash-loans/${id}`);
            const loan = res.data;

            setForm({
                branchname: loan.branch_id || '',
                groupname: loan.group_id || '',
                membername: loan.member_id || '',
                unit: 1,
                loanamountwithint: loan.loan_amount_with_interest || '',
                opendate: loan.open_date || '',
                guarantor: loan.guarantor2_id || '',
                staffid: loan.staff_id || '',
                membernames: '', // You can customize fetching member full name if needed
                loanamount: loan.loan_amount || '',
                coapplicantmember: loan.coapplicant_id || '',
                guarantors: loan.guarantor1_id || '',
                tenure: loan.tenure || '',
                collectiontype: loan.collection_type || 'Weekly',
                description: loan.description || '',
                close_day: loan.close_day || '',
                status: loan.status || 'active',
                emi:loan.emi || '0',
            });

            if (loan.branch_id) {
                const groupsRes = await axios.get(`${API_URL}/branches/${loan.branch_id}/groups`);
                setGroups(groupsRes.data);
            }
            if (loan.group_id) {
                const membersRes = await axios.get(`${API_URL}/groups/${loan.group_id}/members`);
                setMembers(membersRes.data);
            }
        } catch (error) {
            console.error('Failed to load product loan', error);
            Swal.fire("Error", "Failed to load product loan data", "error");
        }
    };

    const fetchInitialData = async () => {
        try {
            const [branchRes, productRes, staffRes] = await Promise.all([
                axios.get(`${API_URL}/branches`),
                axios.get(`${API_URL}/products`),
                axios.get(`${API_URL}/employees`)
            ]);

            setBranches(branchRes.data.data ?? branchRes.data);
            setProducts(productRes.data.data ?? productRes.data);
            setStaffs(staffRes.data.data ?? staffRes.data);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Failed to load initial dropdown data", "error");
        }
    };

    const handleBranchChange = async (option) => {
        setForm({ ...form, branchname: option?.value ?? '', groupname: '', membername: '', membernames: '' });
        if (option?.value) {
            try {
                const res = await axios.get(`${API_URL}/branches/${option.value}/groups`);
                setGroups(res.data);
                setMembers([]);
            } catch (error) {
                console.error("Error loading groups:", error);
            }
        }
    };

    const handleGroupChange = async (option) => {
        setForm({ ...form, groupname: option?.value ?? '', membername: '', membernames: '' });
        if (option?.value) {
            try {
                const res = await axios.get(`${API_URL}/groups/${option.value}/members`);
                setMembers(res.data);
            } catch (error) {
                console.error("Error loading members:", error);
            }
        }
    };

    const handleMemberChange = (option) => {
        const memberId = option?.value ?? '';
        const selectedMember = members.find(m => m.id === memberId);
        const fullName = selectedMember ? `${selectedMember.firstname} ${selectedMember.lastname}` : '';
        setForm({ ...form, membername: memberId, membernames: fullName });
    };

    const handleSelectChange = (field) => (option) => {
        setForm({ ...form, [field]: option?.value ?? '' });
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        const val = type === 'radio' ? value : value;

        if (name === 'productid') {
            const selectedProduct = products.find(p => p.id.toString() === value.toString());
            setForm(prev => ({
                ...prev,
                [name]: value,
                loanamount: selectedProduct ? selectedProduct.sale_price : ''
            }));
        } else {
            setForm(prev => ({
                ...prev,
                [name]: val
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Map frontend form fields to backend field names
        const payload = {
            branch_id: form.branchname,
            group_id: form.groupname,
            member_id: form.membername,
            staff_id: form.staffid,
            coapplicant_id: form.coapplicantmember,
            guarantor1_id: form.guarantors,
            guarantor2_id: form.guarantor,
            loan_amount: form.loanamount,
            loan_amount_with_interest: form.loanamountwithint,
            tenure: form.tenure,
            collection_type: form.collectiontype,
            open_date: form.opendate,
            description: form.description,
            status: form.status,
            close_day: form.close_day,
            emi:form.emi,
        };

        try {
            if (id) {
                await axios.put(`${API_URL}/cash-loans/${id}`, payload);
                Swal.fire("Success", "Cash Loan updated successfully", "success");
            } else {
                await axios.post(`${API_URL}/cash-loans`, payload);
                Swal.fire("Success", "Cash Loan created successfully", "success");
            }
            navigate('/manage-loan-search');
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Something went wrong", "error");
        }
    };

    // Helper: convert array to react-select format
    const toSelectOptions = (arr, labelKey = 'branchname') =>
        arr.map((item) => ({ value: item.id, label: item[labelKey] }));

    return (
        <form onSubmit={handleSubmit} className="p-4 bg-white rounded shadow-sm">
            <div className="row">
                {/* Left Column */}
                <div className="col-md-6">
                    {/* Branch */}
                    <div className="form-group mb-3">
                        <label>Branch</label>
                        <Select
                            options={toSelectOptions(branches)}
                            value={toSelectOptions(branches).find(opt => opt.value === form.branchname)}
                            onChange={handleBranchChange}
                            placeholder="Select Branch"
                        />
                    </div>
                    {/* Group */}
                    <div className="form-group mb-3">
                        <label>Group</label>
                        <Select
                            options={toSelectOptions(groups, 'groupname')}
                            value={toSelectOptions(groups, 'groupname').find(opt => opt.value === form.groupname)}
                            onChange={handleGroupChange}
                            placeholder="Select Group"
                        />
                    </div>

                    {/* Member */}
                    <div className="form-group mb-3">
                        <label>Member</label>
                        <Select
                            options={toSelectOptions(members, 'firstname')}
                            value={toSelectOptions(members, 'firstname').find(opt => opt.value === form.membername)}
                            onChange={handleMemberChange}
                            placeholder="Select Member"
                        />
                    </div>

                    {/* Unit */}
                    <div className="form-group mb-3">
                        <label>Unit</label>
                        <input type="number" className="form-control" name="unit" value={form.unit} onChange={handleChange} />
                    </div>


                        <div className="form-group mb-3">
                            <label>EMI</label>
                            <input
                                type="number"
                                className="form-control"
                                name="emi"
                                value={form.emi}
                                onChange={handleChange}
                                readOnly // Optional: prevent user edits if EMI is auto-calculated
                            />
                        </div>


                    {/* Product */}
                    <div className="form-group mb-3">
                        <label>Loan Amount</label>
                        <input type="text" className="form-control" name="loanamount" value={form.loanamount} onChange={handleChange} />
                    </div>


                    {/* Open Date */}
                    <div className="form-group mb-3">
                        <label>Open Date</label>
                        <input type="date" className="form-control" name="opendate" value={form.opendate} onChange={handleChange} />
                    </div>

                    {id && (
                        <>
                            {/* Close Date */}
                            <div className="form-group mb-3">
                                <label>Close Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="close_day"
                                    value={form.close_day}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* Status */}
                            <div className="form-group mb-3">
                                <label>Status</label>
                                <select
                                    className="form-control"
                                    name="status"
                                    value={form.status}
                                    onChange={handleChange}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="active">Active</option>
                                    <option value="closed">Closed</option>
                                </select>
                            </div>
                        </>
                    )}

                </div>

                {/* Right Column */}
                <div className="col-md-6">
                    {/* Staff */}
                    <div className="form-group mb-3">
                        <label>Staff</label>
                        <Select
                            options={toSelectOptions(staffs, 'firstname')}
                            value={toSelectOptions(staffs, 'firstname').find(opt => opt.value === form.staffid)}
                            onChange={handleSelectChange('staffid')}
                            placeholder="Select Staff"
                        />
                    </div>

                    {/* Member Name */}
                    <div className="form-group mb-3">
                        <label>Member Name</label>
                        <input type="text" className="form-control" value={form.membernames} readOnly />
                    </div>

                    {/* Loan Amount */}
                    <div className="form-group mb-3">
                        <label>Loan Amount with interest</label>
                        <input type="number" className="form-control" name="loanamountwithint" value={form.loanamountwithint} onChange={handleChange} />
                    </div>

                    {/* Co-Applicant */}
                    <div className="form-group mb-3">
                        <label>Co-Applicant</label>
                        <Select
                            options={toSelectOptions(members, 'firstname')}
                            value={toSelectOptions(members, 'firstname').find(opt => opt.value === form.coapplicantmember)}
                            onChange={handleSelectChange('coapplicantmember')}
                            placeholder="Select Co-Applicant"
                        />
                    </div>

                    {/* Guarantors */}
                    <div className="form-group mb-3">
                        <label>Guarantor 1</label>
                        <Select
                            options={toSelectOptions(members, 'firstname')}
                            value={toSelectOptions(members, 'firstname').find(opt => opt.value === form.guarantors)}
                            onChange={handleSelectChange('guarantors')}
                            placeholder="Select Guarantor 1"
                        />
                    </div>

                    <div className="form-group mb-3">
                        <label>Guarantor 2</label>
                        <Select
                            options={toSelectOptions(members, 'firstname')}
                            value={toSelectOptions(members, 'firstname').find(opt => opt.value === form.guarantor)}
                            onChange={handleSelectChange('guarantor')}
                            placeholder="Select Guarantor 2"
                        />
                    </div>

                    {/* Tenure */}
                    <div className="form-group mb-3">
                        <label>Tenure (weeks)</label>
                        <input type="number" className="form-control" name="tenure" value={form.tenure} onChange={handleChange} />
                    </div>

                    {/* Collection Type */}
                    <div className="form-group mb-3">
                        <label>Collection Type</label><br />
                        {['Daily', 'Weekly', 'Monthly'].map(type => (
                            <div key={type} className="form-check checked-secondary d-flex align-items-center gap-2 me-3" style={{ display: 'inline-flex' }}>
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="collectiontype"
                                    id={`collectiontype-${type}`}
                                    value={type}
                                    checked={form.collectiontype === type}
                                    onChange={handleChange}
                                />
                                <label
                                    className="form-check-label line-height-1 fw-medium text-secondary-light"
                                    htmlFor={`collectiontype-${type}`}
                                    style={{ marginBottom: 0 }}
                                >
                                    {type}
                                </label>
                            </div>
                        ))}
                    </div>
                    {/* Description */}
                    <div className="form-group mb-3">
                        <label>Description</label>
                        <textarea className="form-control" name="description" value={form.description} onChange={handleChange}></textarea>
                    </div>
                </div>
            </div>

            {/* Submit Button */}
            <div className="text-center mt-3">
                <button type="submit" className="btn btn-primary px-4">Apply</button>
            </div>
        </form>
    );
};

export default AddCashLoanLayer;
